import { useState, useEffect } from "react"
import { API_ORIGIN } from "@/constants"
import { OnboardingStep } from "@/hooks/use-onboarding"
import { useProfile, candidateSections } from "@/hooks/use-profile"
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { FormField } from "@/components/ui/form-field"
import { SkillChipField } from '@/components/skills/skill-chip-field'
import { appendSkillName } from '@/utils/profile-skill-suggestions'
import { OnboardingStepNext, runOnboardingStepNext } from "@/lib/onboarding-step-nav"

interface SkillsStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

export function SkillsStep({ onNext, onBack, isCompleting, canGoBack }: SkillsStepProps) {
  const { profile } = useProfile()
  const candidate = candidateSections(profile)
  const [skills, setSkills] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [skillInputError, setSkillInputError] = useState<string | null>(null)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    if (hydrated || !candidate.skills?.length) return
    setSkills(candidate.skills)
    setHydrated(true)
  }, [candidate.skills, hydrated])

  const handleAddSkill = (rawName: string) => {
    const result = appendSkillName(skills, rawName)
    if (!result.ok) {
      setSkillInputError(result.error)
      return
    }
    setSkills(result.skills)
    setSkillInputError(null)
  }

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkills((current) => current.filter((s) => s !== skillToRemove))
    setSkillInputError(null)
  }

  const handleContinue = async () => {
    setSaving(true)
    setError(null)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_ORIGIN}/api/profiles/onboarding/skills`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ skills }),
      })

      if (response.ok) {
        await runOnboardingStepNext(onNext, setError)
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.error || 'Failed to save skills')
      }
    } catch (err) {
      console.error('Error saving skills:', err)
      setError('Network error. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <FormField label="Add skills" htmlFor="skill-input">
          <SkillChipField
            id="skill-input"
            skills={skills}
            profileSkills={candidate.skills}
            disabled={saving || isCompleting}
            onAdd={handleAddSkill}
            onRemove={handleRemoveSkill}
          />
        </FormField>

        {skillInputError ? <p className="text-sm text-destructive">{skillInputError}</p> : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleContinue}
        backDisabled={saving || isCompleting}
        continueDisabled={saving || isCompleting}
        isBusy={saving || isCompleting}
        continueLabel="Finish"
        continueBusyLabel="Finishing…"
      />
    </div>
  )
}
